import Column from '@amzn/meridian/column';
import Table, {
  TableActionBar,
  TableCell,
  TableRow,
} from '@amzn/meridian/table';
import Text from '@amzn/meridian/text';
import Box from '@amzn/meridian/box';
import Row from '@amzn/meridian/row';
import moment from 'moment';
import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { FormattedMessage, FormattedPlural, useIntl } from 'react-intl';
import { useLocation } from 'react-router';

import {
  ConsolidatedContact,
  ContactStatusChangeInput,
  ContactStatusOptionsObject,
  ContactViewTabObject,
} from '../../../../api/CoachingNotes/schema';
import useCustomTracking from '../../../../Util/useCustomTracking';
import {
  customSort,
  getContactStatusI18nKey,
  NoContactView,
  NOT_FOUND_LABEL,
  SortableColumn,
  SortableDirection,
  TabProps,
} from './TabHelper';
import { ReviewedTabFilter } from './Filter/ReviewedTabFilter';
import ExpandableRow from './Expander/ExpandableRow';
import { convertReviewToDisplay } from './Expander/ExpanderContainer';
import FieldArrayDisplay from '../../../CoachingForm/LayoutComponents/FieldArrayDisplay';
import Pagination from '@amzn/meridian/pagination';
import Link from '@amzn/meridian/link';
import DataSource from '../../../../api';
import { Feature, isFeatureEnabled } from '../../../../config/FeaturesConfig';
import { UpdateFormReviewInput } from '../../../../api/FormLibrary/schema';
import { genericFormParser } from '../../../FormLibrary/utils/formDetailsParser';
import DataState from '../../../../Types/DataState';

const ReviewedTab: React.FC<TabProps> = ({
  contacts,
  refreshContacts,
  username,
  sourcePage,
  isTm,
}: TabProps): React.ReactElement => {
  const intl = useIntl();
  const apiNaws = DataSource.getDataFeed('NAWS');
  const [sortColumn, setSortColumn] = useState(
    SortableColumn.LAST_MODIFIED_DATE
  );
  const [sortDirection, setSortDirection] = useState(
    SortableDirection.DECENDING
  );
  const [, setSubmitSaveState] = useState('');
  const [formStatusCode, setFormStatusCode] = useState({});
  const [formStatusCodeState, setFormStatusCodeState] = useState(
    DataState.INITIALIZED
  );

  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const contactReviewId = searchParams.get('contactReviewId') || undefined;

  const [filteredContacts, setFilteredContacts] = useState(contacts);

  const sortedContacts = useMemo(() => {
    return [...filteredContacts].sort(
      (noteA: ConsolidatedContact, noteB: ConsolidatedContact) =>
        customSort(sortColumn, sortDirection, noteA, noteB)
    );
  }, [filteredContacts, sortColumn, sortDirection]);

  const itemsPerPage = 10;
  const [currentPage, setCurrentPage] = useState(1);

  const firstVisibleIndex = (currentPage - 1) * itemsPerPage;
  const lastVisibleIndex = firstVisibleIndex + itemsPerPage;

  const pageCount = Math.max(
    1,
    Math.ceil(sortedContacts.length / itemsPerPage)
  );

  // eslint-disable-next-line no-shadow
  const onSort = useCallback(
    ({
      sortColumn,
      sortDirection,
    }: {
      sortColumn: SortableColumn;
      sortDirection: SortableDirection;
    }) => {
      setSortDirection(sortDirection);
      setSortColumn(sortColumn);
    },
    []
  );

  const [TableSortTrackingTag, tableSortClick] = useCustomTracking(
    'coaching-notes-reviewed-table-sort',
    'coaching-notes-reviewed-table-sort',
    {
      'data-csa-c-type': 'table',
      'data-csa-c-sort-column': sortColumn || '',
      'data-csa-c-sort-order': sortDirection || '',
      'data-csa-c-tag1': sourcePage || '',
      'data-csa-c-tag2': 'Contact Table',
      'data-csa-c-tag3': 'REVIEWED',
    }
  );

  useEffect(() => {
    tableSortClick();
  }, [sortColumn, sortDirection]);

  if (contacts.length === 0) {
    return <NoContactView />;
  }

  const handleSubmit = async (contactId: String): Promise<any> => {
    const contact = contacts.filter(
      contact => contact.contactID === contactId
    )[0];

    let contactTab;
    if (contact.recommended !== 0) {
      contactTab = ContactViewTabObject.RECOMMENDED;
    } else if (contact.recent !== 0) {
      contactTab = ContactViewTabObject.RECENT;
    } else if (
      contact.saved !== 0 &&
      (contact.contactStatus === ContactStatusOptionsObject.DRAFT ||
        contact.contactStatus === null)
    ) {
      contactTab = ContactViewTabObject.SAVED;
    }

    const newContactDetailsInput = apiNaws.prepareContactDetailsQuery(
      contact.contactInfo,
      (contact as ConsolidatedContact).recommended,
      contactTab,
      contact.saved,
      contact.saveOrigin
    );
    const newValues: ContactStatusChangeInput = {
      userName: username,
      userID: contact.userID,
      year: contact.calYear,
      week: contact.calWeek,
      contactID: contact.contactID,
      reviewStatus: contact.status,
      contactDetailsInput: newContactDetailsInput,
      contactStatus: ContactStatusOptionsObject.REVIEWED,
    };

    if (contact.contactInfo) {
      if (!contact.formReviews) {
        await apiNaws.updateContactRecommendation(
          newValues,
          setSubmitSaveState
        );
      } else {
        const updateFormReviewInput: UpdateFormReviewInput = {
          userName: username,
          userID: contact.userID,
          year: contact.calYear,
          week: contact.calWeek,
          contactID: contact.contactID,
          reviewStatus: ContactStatusOptionsObject.REVIEWED,
          contactDetailsInput: newContactDetailsInput,
          isReviewedByCSA: true,
          contactStatus: ContactStatusOptionsObject.REVIEWED,
          formDetails: [genericFormParser(newValues, contact)],
        };

        await apiNaws.updateFormReview(
          updateFormReviewInput,
          setFormStatusCode,
          setFormStatusCodeState
        );
      }
    } else {
      const updateFormReviewInput: UpdateFormReviewInput = {
        userName: username,
        userID: contact.userID,
        year: contact.calYear,
        week: contact.calWeek,
        contactID: contact.contactID,
        reviewStatus: ContactStatusOptionsObject.REVIEWED,
        contactDetailsInput: newContactDetailsInput,
        isReviewedByCSA: true,
        contactStatus: ContactStatusOptionsObject.REVIEWED,
        formDetails: [genericFormParser(newValues, contact)],
      };

      await apiNaws.updateFormReview(
        updateFormReviewInput,
        setFormStatusCode,
        setFormStatusCodeState
      );
    }

    if (refreshContacts) refreshContacts();
    return true;
  };

  return (
    <Column width='100%' alignmentHorizontal='center' spacing='300'>
      <Table
        headerRows={1}
        showDividers
        sortColumn={sortColumn}
        sortDirection={
          sortDirection === SortableDirection.ASCENDING
            ? 'ascending'
            : 'descending'
        }
        onSort={onSort}
        rowComponents={[ExpandableRow, TableRow]}
        layout='fixed'
      >
        <TableSortTrackingTag />
        <TableActionBar>
          <ReviewedTabFilter
            contacts={contacts}
            setFilteredContacts={setFilteredContacts}
            setCurPage={setCurrentPage}
            sortColumn={sortColumn}
            sortDirection={sortDirection}
            sourcePage={sourcePage}
            contactReviewId={contactReviewId}
          />
        </TableActionBar>
        <TableRow highlightOnHover={false}>
          <TableCell width='11%' sortColumn={SortableColumn.LAST_MODIFIED_DATE}>
            <FormattedMessage
              id='cs_perfportal_coaching_lastUpdate'
              defaultMessage='Last update'
            />
          </TableCell>
          <TableCell width='55%' sortColumn={SortableColumn.CONTACT_ID}>
            <FormattedMessage
              id='PerfPortal_contactId'
              defaultMessage='Contact ID'
            />
          </TableCell>
          <TableCell width='18%' sortColumn={SortableColumn.LAST_MODIFIED_USER}>
            <FormattedMessage
              id='cs_perfportal_coaching_history_coach'
              defaultMessage='Coach'
            />
          </TableCell>
          <TableCell width='11%'>
            <FormattedMessage
              id='cs_perfportal_status'
              defaultMessage='Status'
              description='Status for Contacts'
            />
          </TableCell>
          <TableCell />
        </TableRow>

        {sortedContacts
          .slice(firstVisibleIndex, lastVisibleIndex)
          .map((contact: ConsolidatedContact, index: number) => {
            const contactStatus =
              contact.contactStatus!.charAt(0) +
              contact.contactStatus!.slice(1).toLowerCase();

            const contactID = contact.contactID;
            const isHighlight: boolean = index % 2 !== 0;

            return (
              <ExpandableRow
                key={`${currentPage}_${index}_${contact.contactID}`}
                rowCount={index}
                rowComponents={[ExpandableRow, TableRow]}
                currentPage={currentPage}
                currentSortColumn={sortColumn}
                currentSortDirection={sortDirection}
                isExpandable={true}
              >
                <TableCell data-cy='expandable-cell' width='13%'>
                  {moment(contact.contactInfo?.lastModifiedDate).format(
                    'YYYY-MM-DD'
                  )}
                </TableCell>
                <TableCell width='13%'>{contact.contactID}</TableCell>
                <TableCell width='13%'>
                  <Text>
                    {contact.contactDeliveredUsername ??
                      contact.contactInfo?.lastModifiedUser}
                  </Text>
                </TableCell>

                <TableCell width='13%'>
                  <Text data-cy={`contact-status-${contact.contactID}`}>
                    {/* TODO - Remove the nested conditon after DE - Form Library Release - Bandaid for corner case */}
                    <FormattedMessage
                      id={getContactStatusI18nKey(
                        (contactStatus === 'Draft'
                          ? ''
                          : contact.contactStatus) ?? ''
                      )}
                      defaultMessage={
                        (contactStatus === 'Draft'
                          ? NOT_FOUND_LABEL
                          : contactStatus) ?? NOT_FOUND_LABEL
                      }
                    />
                  </Text>
                </TableCell>

                <TableCell>
                  {contactStatus !== null &&
                    contactStatus !== undefined &&
                    contact.contactStatus !==
                      ContactStatusOptionsObject.REVIEWED &&
                    sourcePage !== 'Team' && (
                      <Link
                        data-csa-c-type='link'
                        data-csa-c-slot-id={`csa-contact-table-Review-action-link-slot`}
                        data-csa-c-content-id={`csa-contact-table-Review-action-link`}
                        data-csa-c-action={`Review`}
                        data-csa-c-tag1={sourcePage || ''}
                        data-csa-c-tag2='CSA Contact Review Table'
                        data-csa-c-contactid={contact.contactID}
                        data-cy={`contact-table-form-Review-action-link-${contact.contactID}`}
                        href={contact.contactID}
                        onClick={handleSubmit}
                        type='secondary'
                      >
                        <FormattedMessage
                          id='cs_perfportal_coaching_review'
                          defaultMessage='Review'
                        />
                      </Link>
                    )}
                </TableCell>

                {(contact.contactStatus ===
                  ContactStatusOptionsObject.REVIEWED ||
                  sourcePage === 'Team') && (
                  <TableRow
                    backgroundColor={isHighlight ? 'secondary' : 'primary'}
                  >
                    <TableCell />
                    <TableCell>
                      <Column>
                        <FieldArrayDisplay
                          mapKey={`contactReview_${currentPage}_${index}_${contactID}`}
                          displayValues={convertReviewToDisplay(
                            contact,
                            intl,
                            true,
                            isTm
                          )}
                        />
                      </Column>
                    </TableCell>
                    <TableCell />
                    <TableCell />
                    <TableCell />
                  </TableRow>
                )}
              </ExpandableRow>
            );
          })}
      </Table>
      <Row
        width='50%'
        widths={['grid-4', 'grid-8']}
        alignmentVertical='center'
        spacing='300'
      >
        <Box>
          <Text alignment='right'>
            {sortedContacts.length}{' '}
            <FormattedPlural
              value={sortedContacts.length}
              one={
                <FormattedMessage
                  id='cs_perfportal_coaching_note_result'
                  defaultMessage='result'
                />
              }
              other={
                <FormattedMessage
                  id='cs_perfportal_coaching_note_results'
                  defaultMessage='results'
                />
              }
            />
          </Text>
        </Box>
        <Box>
          <Pagination
            showSkipArrows
            numberOfPages={pageCount}
            onChange={setCurrentPage}
            currentPage={currentPage}
          />
        </Box>
      </Row>
    </Column>
  );
};

export default ReviewedTab;
